'use client'

import { Children } from 'react'
import * as styles from './Button.css'
import Link, { type LinkProps } from 'next/link'
import { Button as BaseButton } from '@ariakit/react'
import { type RecipeVariants } from '@vanilla-extract/recipes'
import { type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'

type ButtonVariants = RecipeVariants<typeof styles.buttonRecipe>

// Icon size mapping for different button sizes
const iconSizeMap = {
  tiny: 16,
  small: 16,
  medium: 20,
  large: 20,
} as const

export type ButtonProps = {
  as?: React.ElementType
  /** When `href` is present a `a` tag is rendered together with Next.js's Link component */
  href?: LinkProps['href']
  /** If only an icon is provided and no children, then `square` is set to `true` automatically */
  icon?: LucideIcon
  /** Same props as Next.js's Link component */
  linkProps?: Omit<LinkProps, 'href'>
  /** Optional tooltip content rendered with Ariakit Tooltip */
  tooltip?: React.ReactNode
} & ButtonVariants &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    ref?: React.RefObject<HTMLButtonElement | null>
  }

export default function Button({
  children,
  className = '',
  size = 'medium',
  variant = 'raised',
  circular = false,
  square = false,
  icon: Icon,
  href,
  linkProps = {},
  as: As = 'button',
  tooltip,
  ...props
}: ButtonProps) {
  const isIconOnly =
    Icon !== undefined && Children.toArray(children).length === 0
  square = isIconOnly || square

  const isExternal = typeof href === 'string' && href.startsWith('https://')
  if (isExternal) {
    props.rel = 'noopener'
    props.target = '_blank'
  }

  const button = (
    <BaseButton
      render={(props) =>
        href !== undefined ? (
          <Link {...linkProps} href={href} {...props} />
        ) : (
          <As {...props} />
        )
      }
      className={`${styles.buttonRecipe({ size, variant, circular, square })} ${className}`}
      {...props}
    >
      <>
        {Icon && <Icon size={iconSizeMap[size]} />}
        {children}
      </>
    </BaseButton>
  )

  if (tooltip === undefined) return button

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
